import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast, Toaster } from 'sonner';
import { Calendar, Clock, Building2, User, CheckCircle, AlertCircle, RefreshCw, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ReschedulePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [showRescheduleForm, setShowRescheduleForm] = useState(false);
  const [error, setError] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [formData, setFormData] = useState({
    requested_date: '',
    requested_time: '',
    reason: ''
  });

  useEffect(() => {
    const fetchTokenInfo = async () => {
      try {
        const res = await axios.get(`${API}/reschedule/token/${token}`);
        setTokenInfo(res.data);
        // Check if already accepted
        if (res.data.accepted) {
          setAccepted(true);
        }
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired link');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchTokenInfo();
    }
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const res = await axios.post(`${API}/schedule/accept/${token}`);
      if (res.data.already_accepted) {
        toast.info('You have already confirmed this schedule');
      } else {
        toast.success('Schedule confirmed successfully!');
      }
      setAccepted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm schedule');
    } finally {
      setAccepting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.requested_date || !formData.requested_time) {
      toast.error('Please select both date and time');
      return;
    }

    if (!formData.reason || formData.reason.trim().length < 5) {
      toast.error('Please provide a reason for rescheduling');
      return;
    }

    setSubmitting(true);
    try {
      const requestedDateTime = new Date(`${formData.requested_date}T${formData.requested_time}`);
      
      await axios.post(`${API}/reschedule/request/${token}`, {
        performance_id: '',
        session_type: tokenInfo.session_type,
        requested_datetime: requestedDateTime.toISOString(),
        reason: formData.reason
      });

      setSubmitted(true);
      toast.success('Reschedule request submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit reschedule request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-rose-100 flex items-center justify-center p-4">
        <Toaster richColors position="top-center" />
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Unable to Process</h2>
            <p className="text-gray-600">{error}</p>
            <Button 
              className="mt-6"
              onClick={() => navigate('/')}
            >
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Toaster richColors position="top-center" />
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Request Submitted!</h2>
            <p className="text-gray-600 mb-4">
              Your reschedule request has been submitted successfully. You will receive an email once it's approved.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
              <p className="text-sm text-gray-600">
                <strong>Session:</strong> {tokenInfo?.session_label}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Requested Time:</strong> {formData.requested_date} at {formData.requested_time}
              </p>
              {formData.reason && (
                <p className="text-sm text-gray-600">
                  <strong>Reason:</strong> {formData.reason}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Toaster richColors position="top-center" />
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="h-20 w-20 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-4">
              <Check className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Schedule Confirmed!</h2>
            <p className="text-gray-600 mb-4">
              Thank you for confirming your {tokenInfo?.session_label} session.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
              <p className="text-sm text-gray-600 flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-green-600" />
                <strong>Session:</strong> {tokenInfo?.session_label}
              </p>
              {tokenInfo?.original_datetime && (
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-green-600" />
                  <strong>Scheduled:</strong> {format(new Date(tokenInfo.original_datetime), 'PPpp')}
                </p>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-4">
              We look forward to seeing you!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Toaster richColors position="top-center" />
      <Card className="w-full max-w-lg shadow-xl">
        <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-6 w-6" />
            Schedule Response
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {/* Current Session Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">Session Details</h3>
            <div className="space-y-2 text-sm">
              <p className="flex items-center gap-2">
                <User className="h-4 w-4 text-blue-500" />
                <span className="text-gray-600">Candidate:</span>
                <span className="font-medium">{tokenInfo?.candidate_name}</span>
              </p>
              {tokenInfo?.company_name && (
                <p className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-blue-500" />
                  <span className="text-gray-600">Company:</span>
                  <span className="font-medium">{tokenInfo?.company_name}</span>
                </p>
              )}
              <p className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-500" />
                <span className="text-gray-600">Session:</span>
                <span className="font-medium">{tokenInfo?.session_label}</span>
              </p>
              {tokenInfo?.original_datetime && (
                <p className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-amber-500" />
                  <span className="text-gray-600">Scheduled:</span>
                  <span className="font-medium text-amber-700">
                    {format(new Date(tokenInfo.original_datetime), 'MMMM dd, yyyy')} at{' '}
                    {format(new Date(tokenInfo.original_datetime), 'hh:mm a')}
                  </span>
                </p>
              )}
            </div>
          </div>

          {/* Accept / Reschedule Buttons */}
          {!showRescheduleForm ? (
            <div className="space-y-4">
              <p className="text-gray-600 text-center mb-4">Please confirm your attendance or request a reschedule</p>
              
              <Button 
                onClick={handleAccept}
                disabled={accepting}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
              >
                {accepting ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Confirming...
                  </span>
                ) : (
                  <span className="flex items-center gap-2 justify-center">
                    <Check className="h-6 w-6" />
                    Accept Schedule
                  </span>
                )}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">or</span>
                </div>
              </div>

              <Button 
                variant="outline"
                onClick={() => setShowRescheduleForm(true)}
                className="w-full border-amber-500 text-amber-600 hover:bg-amber-50 py-6 text-lg"
              >
                <span className="flex items-center gap-2 justify-center">
                  <RefreshCw className="h-5 w-5" />
                  Request Reschedule
                </span>
              </Button>
            </div>
          ) : (
            /* Reschedule Form */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800">Request New Time</h3>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowRescheduleForm(false)}
                >
                  ← Back
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date">Preferred Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.requested_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, requested_date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="time">Preferred Time *</Label>
                  <Input
                    id="time"
                    type="time"
                    value={formData.requested_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, requested_time: e.target.value }))}
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="reason">Reason for Rescheduling *</Label>
                <Textarea
                  id="reason"
                  placeholder="Please explain why you need to reschedule..."
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  required
                  className="mt-1"
                  rows={3}
                />
              </div>

              <Button 
                type="submit" 
                disabled={submitting}
                className="w-full bg-amber-600 hover:bg-amber-700"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Submitting...
                  </span>
                ) : (
                  'Submit Reschedule Request'
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
